// Moteur PUR de roulement (roll-forward) des créances / dettes prévisionnelles —
// partagé par le Bilan prévisionnel ET le Plan de liquidité (direct), pour que
// les deux vues reposent EXACTEMENT sur le même calcul.
//   solde[m] = solde[m-1] + nouvelles[m] − arrangements[m] − encPO[m] − encNouv[m]

// std_key → configuration d'affichage du roulement.
export const ROLLFORWARD = {
  ar_clients:   { flow: 'revenue', sign: 1,  dsoName: 'DSO', defaultDso: 45, newLabel: 'Nouvelles ventes (TTC)', openLabel: 'Encaissement postes ouverts', dsoLabel: 'Encaissement nouvelles ventes', arrLabel: 'Arrangements clients' },
  ap_suppliers: { flow: 'purch',   sign: -1, dsoName: 'DPO', defaultDso: 30, newLabel: 'Nouveaux achats (TTC)',  openLabel: 'Paiement postes ouverts',    dsoLabel: 'Paiement nouveaux achats',    arrLabel: 'Arrangements fournisseurs' },
};
export const DEFAULT_PROFILE = [50, 50];   // % encaissé à M+1, M+2 (postes ouverts)
export const DEFAULT_DSO = 45;             // délai d'encaissement des nouvelles ventes (jours)
// Achats : 30 jours = le mois d'achat entier reste dû, réglé le mois suivant.
// Seules les CHARGES DIRECTES transitent par les dettes fournisseurs ; les
// autres charges d'exploitation (loyer, assurances, abonnements) sont payées
// le mois même et ne créent pas de dette.
export const DEFAULT_DPO = 30;
export const defaultDelay = (stdKey) => ROLLFORWARD[stdKey]?.defaultDso ?? DEFAULT_DSO;

// Roulement d'une ligne de créances/dettes sur les mois prévisionnels.
// - opening      : solde réel au dernier mois clôturé (départ du roulement).
// - flowTTC[12]  : flux P&L brutifié TTC (ventes/achats) par mois calendaire.
// - arrangements : { mois(1..12): montant } encaissements manuels (plans de paiement).
// - profileOpen[]: % encaissé par décalage (M+1, M+2…) des POSTES OUVERTS.
// - dsoNew       : délai d'encaissement des NOUVELLES ventes en jours (countback).
// - buckets      : PANIERS de facturation [{ days, flow[12], collect?[12] }].
//                  Fournis, ils REMPLACENT le couple (flowTTC, dsoNew). Un panier
//                  à `collect` (2026-09-12, le plan des ventes connecté) encaisse
//                  ce que le plan dit, mois par mois, sans délai : sa créance est
//                  ce qui est facturé et pas encore encaissé.
// → out[m] = { balance, newSales, arrangement, encPO, encNouv } pour m = closed+1..12.
export function computeRollforward({ opening, closed, flowTTC, arrangements, profileOpen, dsoNew, buckets }) {
  const PO = (profileOpen && profileOpen.length ? profileOpen : DEFAULT_PROFILE).map((x) => (Number(x) || 0) / 100);
  const arr = arrangements || {};
  const totalArr = Object.values(arr).reduce((s, v) => s + (Number(v) || 0), 0);
  const openingBase = opening - totalArr;
  const first = closed + 1;
  const MD = 360 / 12;
  const dso = Number(dsoNew) || 0;
  const bkts = (Array.isArray(buckets) && buckets.length)
    ? buckets.map((b) => ({ days: Number(b.days) || 0, flow: b.flow || [], collect: Array.isArray(b.collect) ? b.collect : null }))
    : [{ days: dso, flow: flowTTC || [], collect: null }];
  // Panier à encaissements donnés : créance = Σ facturé − Σ encaissé depuis la clôture.
  const arPlan = (mIdx, b) => {
    let bal = 0;
    for (let k = closed; k <= mIdx; k++) bal += (Number(b.flow[k]) || 0) - (Number(b.collect[k]) || 0);
    return bal;
  };
  const arOne = (mIdx, flow, days) => {
    if (days <= 0) return 0;
    let rem = days, bal = 0, k = mIdx;
    while (rem > 1e-9 && k >= closed) {
      const take = Math.min(rem, MD);
      bal += (Number(flow[k]) || 0) * (take / MD);
      rem -= take;
      k -= 1;
    }
    return bal;
  };
  const arBucket = (mIdx, b) => (b.collect ? arPlan(mIdx, b) : arOne(mIdx, b.flow, b.days));
  const arNew = (mIdx) => bkts.reduce((s, b) => s + arBucket(mIdx, b), 0);
  const flowAt = (mIdx) => bkts.reduce((s, b) => s + (Number(b.flow[mIdx]) || 0), 0);
  // Encaissement PANIER PAR PANIER : flux du mois − variation de sa créance.
  // La somme vaut exactement `encNouv` (même arithmétique), donc le plan de
  // liquidité peut dire QUI encaisse quoi sans second calcul.
  const encPerBucket = (m, prevArs) => bkts.map((b, i) => {
    const now = arBucket(m - 1, b);
    return { now, enc: (Number(b.flow[m - 1]) || 0) - (now - (prevArs[i] || 0)) };
  });
  const out = {};
  let prev = opening;
  let prevAr = 0;
  let prevArs = bkts.map(() => 0);
  for (let m = first; m <= 12; m++) {
    const newSales = flowAt(m - 1);
    const arrangement = Number(arr[m]) || 0;
    const encPO = (PO[m - first] || 0) * openingBase;
    const arNow = arNew(m - 1);
    const encNouv = newSales - (arNow - prevAr);
    const balance = prev + newSales - arrangement - encPO - encNouv;
    const per = encPerBucket(m, prevArs);
    out[m] = { balance, newSales, arrangement, encPO, encNouv, encByBucket: per.map((x) => x.enc) };
    prev = balance; prevAr = arNow; prevArs = per.map((x) => x.now);
  }
  return out;
}
